import { Signal, inject } from '@angular/core';

import {
  QueryClient,
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';

import { catchError, firstValueFrom, of } from 'rxjs';

import {
  NomObjectClass,
  NomObjectTypes,
  feedDerivedTimeShown,
} from 'src/app/core/constants';
import { DashboardViewPreset, User } from 'src/app/core/models/core.models';
import {
  FeedSettingsService,
  PropertyViewConfig,
} from 'src/app/core/services/feed-settings.service';
import { HomeSettingsService } from 'src/app/core/services/home-settings.service';
import { UserAccessLevel } from 'src/app/core/services/user.service';
import { WidgetSettingsService } from 'src/app/core/services/widget-settings.service';
import { WorkspaceSettingsService } from 'src/app/core/services/workspace-settings.service';
import {
  ViewId,
  ViewPresetApiService,
  ViewPresetContext,
} from '../services/view-preset-api.service';

/**
 * View presets - the shared tier behind every
 * POST /view_preset/get_by_context_properties read.
 *
 * One endpoint, six contexts (`hv-objects`, `wv-objects`, `pv-widgets`,
 * `pv-feed-results`, `ov-dashboard`, `ov-dashboard-page`) whose `settings` blobs
 * have nothing in common. Hence **one api-service and one save mutation, but a
 * query hook per context**: each blob's post-processing (defaults merge, permission
 * filtering, language fallback) is that context's identity.
 *
 * **The cache is the state.** A toggle patches the cached blob with `setQueryData`
 * and posts the patched value, which is why every query here disables background
 * refetching - a refetch landing on top of a flip would revert it. Nothing rolls
 * back a failed save.
 *
 * **Errors degrade to defaults** rather than surfacing, so `data()` is always
 * eventually defined and no consumer needs a fallback of its own.
 */

/** Scope of a per-user view-options blob (`hv-objects` / `wv-objects`). */
export interface UserViewOptionsScope {
  userId: string;
  /**
   * A key term: the defaults are permission-filtered off `user.feature_components`,
   * which is company-scoped, so this is real identity - and a key change is what
   * makes a live observer refetch.
   */
  companyId: string;
  /**
   * Deliberately **not** a key term: it only feeds the defaults' permission filter,
   * and it is already identified by `userId` + `companyId`.
   */
  user: User;
}

/**
 * `companyId` is passed only for the **user-scoped** blobs, where the same
 * `contextObjectId` (the user) means different things in different companies. An
 * object-scoped context (a dashboard, a feed) is already company-unique, so it omits
 * it - TanStack's key hash drops the undefined entry.
 */
export const viewPresetQueryKey = (
  context: ViewPresetContext,
  companyId?: string,
) =>
  [
    'view_preset',
    context.viewId,
    {
      contextObjectId: context.contextObjectId,
      contextObjectClassId: context.contextObjectClassId,
      companyId,
    },
  ] as const;

const userViewOptionsContext = (
  viewId: ViewId,
  scope: UserViewOptionsScope,
): ViewPresetContext => ({
  viewId,
  contextObjectId: scope.userId,
  contextObjectClassId: NomObjectClass.USER,
});

/**
 * Project the saved `selected` flags onto the defaults. A stored `[]` counts as
 * "never saved" - `Object.keys` of an empty array is 0. Note the `||`: unlike the
 * `pv-widgets` / `pv-feed-results` merges, a default that is selected stays selected
 * whatever the saved blob says.
 */
const mergeViewOptions = (
  defaults: PropertyViewConfig[],
  settings: unknown,
): PropertyViewConfig[] => {
  const isSettingsEmpty =
    !settings ||
    (typeof settings === 'object' && Object.keys(settings).length === 0);

  const saved = (
    isSettingsEmpty ? defaults : (settings as PropertyViewConfig[])
  ) as PropertyViewConfig[];

  return (defaults ?? []).map((option) => {
    const existingOption = saved?.find(
      (v) => v.propertyUri === option.propertyUri,
    );

    return {
      ...option,
      selected: option.selected || existingOption?.selected || false,
    };
  });
};

/**
 * Fetch + merge. `catchError → null` is the degrade-to-defaults path: the merge then
 * runs against an absent blob and yields the defaults.
 */
const fetchViewOptions = (
  api: ViewPresetApiService,
  context: ViewPresetContext,
  defaults: () => Promise<PropertyViewConfig[]>,
): Promise<PropertyViewConfig[]> =>
  Promise.all([
    firstValueFrom(
      api.getViewPresetByContext(context).pipe(catchError(() => of(null))),
    ),
    defaults(),
  ]).then(([viewPreset, defaultConfig]) =>
    mergeViewOptions(defaultConfig, viewPreset?.settings),
  );

function injectUserViewOptionsQuery(
  viewId: ViewId,
  scope: Signal<UserViewOptionsScope | null>,
  defaults: (user: User) => Promise<PropertyViewConfig[]>,
) {
  const api = inject(ViewPresetApiService);

  return injectQuery(() => {
    const current = scope();
    const context = current ? userViewOptionsContext(viewId, current) : null;

    return {
      queryKey: context
        ? viewPresetQueryKey(context, current!.companyId)
        : (['view_preset', viewId, null] as const),
      queryFn: () =>
        fetchViewOptions(api, context!, () => defaults(current!.user)),
      enabled: !!current,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/** Home's view-options dropdown (`hv-objects`). */
export function injectHomeViewOptionsQuery(
  scope: Signal<UserViewOptionsScope | null>,
) {
  const homeSettings = inject(HomeSettingsService);

  return injectUserViewOptionsQuery(ViewId.HomeViewObjects, scope, (user) =>
    homeSettings.getHomeViewConfig(user),
  );
}

/** The workspace screen's view options (`wv-objects`). */
export function injectWorkspaceViewOptionsQuery(
  scope: Signal<UserViewOptionsScope | null>,
) {
  const workspaceSettings = inject(WorkspaceSettingsService);

  return injectUserViewOptionsQuery(
    ViewId.WorkspaceViewObjects,
    scope,
    (user) => workspaceSettings.getWorkspaceViewConfig(user),
  );
}

/**
 * Save one view preset's `settings` blob (POST /view_preset/update_by_id).
 *
 * No toast either way, so a failed save is silent - and it can fail silently
 * *server-side* too: `ViewPresetFacade::processPatchOrPutRequest` swallows a throwing
 * save and still answers 200 with the pre-save row.
 */
export function injectSaveViewPresetMutation() {
  const api = inject(ViewPresetApiService);

  return injectMutation(() => ({
    mutationFn: ({
      context,
      settings,
    }: {
      context: ViewPresetContext;
      settings: unknown;
    }) =>
      firstValueFrom(
        api.saveViewPreset(
          {
            view_id: context.viewId,
            context_object_id: context.contextObjectId,
            context_object_class_id: context.contextObjectClassId,
            name: '',
            settings,
          },
          false,
        ),
      ),
  }));
}

/**
 * Toggle one option of a per-user view-options blob and persist the result. Returns
 * the flipped option so the caller can act on the new value - home's stats option
 * has to invalidate the workspace list.
 */
export function injectToggleUserViewOption(viewId: ViewId) {
  const queryClient = inject(QueryClient);
  const saveMutation = injectSaveViewPresetMutation();

  return (
    scope: UserViewOptionsScope,
    optionName: string,
  ): PropertyViewConfig | undefined => {
    const context = userViewOptionsContext(viewId, scope);
    const queryKey = viewPresetQueryKey(context, scope.companyId);

    const current = queryClient.getQueryData<PropertyViewConfig[]>(queryKey);
    if (!current) {
      return undefined;
    }

    const settings = current.map((option) =>
      option.label === optionName
        ? { ...option, selected: !option.selected }
        : option,
    );

    queryClient.setQueryData(queryKey, settings);
    saveMutation.mutate({ context, settings });

    return settings.find((option) => option.label === optionName);
  };
}

// ─── Dashboard contexts ───────────────────────────────────────────────────
// Both are keyed on the dashboard, so neither takes `companyId`. Unlike the two
// view-options blobs above they carry **scalars, not a defaults-merged array**,
// which is why they get their own hooks rather than a third wrapper.

export const dashboardUIStateContext = (
  dashboardId: string,
): ViewPresetContext => ({
  viewId: ViewId.ObjectViewDashboard,
  contextObjectId: dashboardId,
  contextObjectClassId: NomObjectClass.DASHBOARD,
});

export const dashboardActivePageContext = (
  dashboardId: string,
): ViewPresetContext => ({
  viewId: ViewId.ObjectViewDashboardPage,
  contextObjectId: dashboardId,
  contextObjectClassId: NomObjectClass.DASHBOARD,
});

/** `ov-dashboard` - the page navigator's collapsed/expanded state. */
export function injectDashboardUIStateQuery(
  dashboardId: Signal<string | null>,
) {
  const api = inject(ViewPresetApiService);

  return injectQuery(() => {
    const id = dashboardId();
    const context = id ? dashboardUIStateContext(id) : null;

    return {
      queryKey: context
        ? viewPresetQueryKey(context)
        : (['view_preset', ViewId.ObjectViewDashboard, null] as const),
      queryFn: () =>
        firstValueFrom(
          api.getViewPresetByContext(context!).pipe(catchError(() => of(null))),
        ).then(
          (viewPreset): DashboardViewPreset => ({
            pagination_menu_open:
              (viewPreset?.settings as DashboardViewPreset | undefined)
                ?.pagination_menu_open ?? false,
          }),
        ),
      enabled: !!context,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/** Merge a partial into the cached `ov-dashboard` blob and persist it. */
export function injectUpdateDashboardUIState() {
  const queryClient = inject(QueryClient);
  const saveMutation = injectSaveViewPresetMutation();

  return (dashboardId: string, updates: Partial<DashboardViewPreset>): void => {
    const context = dashboardUIStateContext(dashboardId);
    const queryKey = viewPresetQueryKey(context);

    const settings: DashboardViewPreset = {
      // Fallback for a dashboard with no preset cached yet.
      pagination_menu_open: false,
      ...queryClient.getQueryData<DashboardViewPreset>(queryKey),
      ...updates,
    };

    queryClient.setQueryData(queryKey, settings);
    saveMutation.mutate({ context, settings });
  };
}

/**
 * `ov-dashboard-page` - the dashboard's last selected page.
 *
 * **The one context with no state at all**: its only consumer,
 * `DashboardPageCoordinatorService`, acts on the id once and keeps nothing, and the
 * save is fire-and-forget. Hence the imperative tier rather than a hook - the caller
 * is a root service. The save deliberately does **not** patch the entry: nothing
 * observes it, and the next dashboard open refetches anyway.
 */
export function injectDashboardActivePageTier() {
  const queryClient = inject(QueryClient);
  const api = inject(ViewPresetApiService);
  const saveMutation = injectSaveViewPresetMutation();

  return {
    load: (dashboardId: string): Promise<number | undefined> =>
      queryClient.fetchQuery({
        queryKey: viewPresetQueryKey(dashboardActivePageContext(dashboardId)),
        queryFn: () =>
          firstValueFrom(
            api.getViewPresetByContext(dashboardActivePageContext(dashboardId)),
          ).then(
            (viewPreset) =>
              (viewPreset?.settings as { active_page_id?: number } | undefined)
                ?.active_page_id,
          ),
        staleTime: 0,
        retry: false,
      }),

    save: (dashboardId: string, pageId: number): Promise<unknown> =>
      saveMutation.mutateAsync({
        context: dashboardActivePageContext(dashboardId),
        settings: { active_page_id: pageId },
      }),
  };
}

// ─── `pv-widgets` ─────────────────────────────────────────────────────────
// The one blob carrying **two scopes**: `language` (feeds and dashboards) and
// `editMode` (feeds only - WAP-1180's "per Feed per User" compact widget list).
// One shared write path serves both, which is how a stored `editMode: true` can
// reach a dashboard and disable its widget refreshes. The host check that guards
// against it lives in `WidgetViewPresetService.setEditMode`.

/** What the object's `pv-widgets` preset resolves to, defaults already merged. */
export interface WidgetViewPreset {
  language: PropertyViewConfig[];
  editMode: boolean;
}

/** Identity of a `pv-widgets` blob: any object except a workspace. */
export interface WidgetViewPresetScope {
  objectId: string;
  objectClassId: number;
  user: User;
}

export const widgetViewPresetContext = (
  scope: Pick<WidgetViewPresetScope, 'objectId' | 'objectClassId'>,
): ViewPresetContext => ({
  viewId: ViewId.PanelViewWidgets,
  contextObjectId: scope.objectId,
  contextObjectClassId: scope.objectClassId,
});

/**
 * The object's `pv-widgets` preset. The saved `selected` flag wins only where the
 * property matched; otherwise the default keeps its own.
 */
export function injectWidgetViewPresetQuery(
  scope: Signal<WidgetViewPresetScope | null>,
) {
  const api = inject(ViewPresetApiService);
  const widgetSettings = inject(WidgetSettingsService);

  return injectQuery(() => {
    const current = scope();
    const context = current ? widgetViewPresetContext(current) : null;

    return {
      queryKey: context
        ? viewPresetQueryKey(context)
        : (['view_preset', ViewId.PanelViewWidgets, null] as const),
      queryFn: (): Promise<WidgetViewPreset> =>
        Promise.all([
          firstValueFrom(
            api
              .getViewPresetByContext(context!)
              .pipe(catchError(() => of(null))),
          ),
          widgetSettings.getWidgetLanguageConfig(current!.user),
        ]).then(([viewPreset, defaultLanguageConfig]) => {
          const settings = viewPreset?.settings as
            | { language?: PropertyViewConfig[]; editMode?: boolean }
            | undefined;

          const saved = settings?.language;

          return {
            language: (defaultLanguageConfig ?? []).map((option) => {
              const existingOption = saved?.find(
                (v) => v.propertyUri === option.propertyUri,
              );

              return existingOption
                ? { ...option, selected: existingOption.selected }
                : option;
            }),
            editMode: settings?.editMode ? settings.editMode : false,
          };
        }),
      enabled: !!context,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

// ─── `pv-feed-results` ────────────────────────────────────────────────────
// The feed's card view configuration - the toolbar's View dropdown. Same
// cache-as-state shape as the two view-options blobs, but with the `pv-widgets`
// merge variant, an access-level filter on the defaults, and a radio-group-aware
// toggle.

/** Identity of a `pv-feed-results` blob: the feed plus what the defaults need. */
export interface FeedViewConfigScope {
  feedId: string;
  objectTypeId: NomObjectTypes;
  /**
   * Not a key term: it only feeds the defaults' permission + access-level filtering,
   * and the feed id already identifies the blob company-uniquely.
   */
  user: User;
}

export const feedViewConfigContext = (feedId: string): ViewPresetContext => ({
  viewId: ViewId.PaneViewFeedResults,
  contextObjectId: feedId,
  contextObjectClassId: NomObjectClass.FEED,
});

export interface FeedViewConfig {
  config: PropertyViewConfig[];
  /**
   * True when the feed had a saved view preset, so `config.selected` came from it
   * rather than from the defaults. The `post_time_mode` radio is only trustworthy
   * when this is true - see the toolbar, which otherwise defers to the store's
   * feed-derived `timeShown`.
   */
  fromPreset: boolean;
}

/**
 * **The order below is load-bearing:** iterate the *filtered* defaults and look the
 * saved flags up in them. That is what keeps `post_completeness_score` out of the
 * result for a non-GLOBAL_ADMIN - flip the direction and non-admins get the column
 * back.
 */
const mergeFeedViewConfig = (
  defaults: PropertyViewConfig[],
  settings: unknown,
  user: User,
  feedTimeShown: string,
): FeedViewConfig => {
  const isSettingsEmpty =
    !settings ||
    (typeof settings === 'object' && Object.keys(settings).length === 0);

  const saved = (
    isSettingsEmpty ? defaults : (settings as PropertyViewConfig[])
  ) as PropertyViewConfig[];

  const visible =
    user?.access_level_id < UserAccessLevel.GLOBAL_ADMIN
      ? (defaults ?? []).filter(
          (item) => item.propertyUri !== 'post_completeness_score',
        )
      : (defaults ?? []);

  const merged = visible.map((option) => {
    const existingOption = saved?.find(
      (v) => v.propertyUri === option.propertyUri,
    );

    return existingOption
      ? { ...option, selected: existingOption.selected }
      : option;
  });

  // ! Without a preset the defaults' own `post_time_mode` flag is meaningless -
  // ! it always marks published-absolute, whatever the feed is set to. Point it at
  // ! the feed instead: post cards read the date off these flags in some layouts
  // ! and off the store's `timeShown` in others, and the two have to agree.
  const config = isSettingsEmpty
    ? merged.map((option) =>
        option.radioGroup === 'post_time_mode'
          ? { ...option, selected: option.path === feedTimeShown }
          : option,
      )
    : merged;

  return { config, fromPreset: !isSettingsEmpty };
};

/**
 * The feed results toolbar's View dropdown (`pv-feed-results`). Owned by its two
 * consumers directly (`feed.component` and `feed-results-toolbar`) rather than by a
 * service: nothing outside those two reads it, and component-scoped observers are
 * what give it a per-visit refetch.
 */
export function injectFeedViewConfigQuery(
  feed: Signal<{
    id: string;
    object_type_id: number;
    // Only read to flag the matching `post_time_mode` radio when the feed has no
    // saved preset - see `mergeFeedViewConfig`.
    post_time_field?: string;
    post_time_mode?: string;
  } | null>,
  user: Signal<User | null>,
) {
  const api = inject(ViewPresetApiService);
  const feedSettings = inject(FeedSettingsService);

  return injectQuery(() => {
    const currentFeed = feed();
    const currentUser = user();
    const scope: FeedViewConfigScope | null =
      currentFeed && currentUser
        ? {
            feedId: currentFeed.id,
            objectTypeId: currentFeed.object_type_id,
            user: currentUser,
          }
        : null;
    const context = scope ? feedViewConfigContext(scope.feedId) : null;

    return {
      queryKey: context
        ? viewPresetQueryKey(context)
        : (['view_preset', ViewId.PaneViewFeedResults, null] as const),
      queryFn: (): Promise<FeedViewConfig> =>
        Promise.all([
          firstValueFrom(
            api
              .getViewPresetByContext(context!)
              .pipe(catchError(() => of(null))),
          ),
          feedSettings.getFeedConfig(scope!.objectTypeId, scope!.user),
        ]).then(([viewPreset, defaults]) =>
          mergeFeedViewConfig(
            defaults,
            viewPreset?.settings,
            scope!.user,
            feedDerivedTimeShown(currentFeed!),
          ),
        ),
      enabled: !!context,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    };
  });
}

/**
 * Toggle one option of the feed's view configuration and persist the result.
 *
 * **One quirk, kept deliberately rather than fixed:** a radio option outside
 * the `post_time_mode` group flips nothing at all. The toolbar's
 * `view_posts_mode` radios never reach here (they go through
 * `injectSetFeedPostViewModeMutation`), which is why the gap never showed.
 */
export function injectToggleFeedViewConfigOption() {
  const queryClient = inject(QueryClient);
  const saveMutation = injectSaveViewPresetMutation();

  return (feedId: string, optionName: string): void => {
    const context = feedViewConfigContext(feedId);
    const queryKey = viewPresetQueryKey(context);

    const current = queryClient.getQueryData<FeedViewConfig>(queryKey);
    if (!current) return;

    const option = current.config.find((item) => item.label === optionName);
    if (!option) return;

    const settings = current.config.map((item) => {
      if (!option.isRadio) {
        return item.label === optionName
          ? { ...item, selected: !item.selected }
          : item;
      }

      return option.radioGroup === 'post_time_mode' &&
        item.radioGroup === 'post_time_mode'
        ? { ...item, selected: item.label === optionName }
        : item;
    });

    // `fromPreset` is true from here on: this toggle *is* the preset write, so the
    // config's `post_time_mode` selection is authoritative once it lands.
    queryClient.setQueryData<FeedViewConfig>(queryKey, {
      config: settings,
      fromPreset: true,
    });

    saveMutation.mutate({ context, settings });
  };
}
